import { ExcelPreview } from "@/pages/Projects/Editor/Actions/Export/ExcelPreview";
import type { HighlightArea } from "@react-pdf-viewer/highlight";
import { DocumentType } from "@repo/shared/workflow";
import Papa from "papaparse";
import type React from "react";
import { useEffect } from "react";
import { useState } from "react";
import DevinPdfViewer from "./devinPdfViewer";
import { LoadingSpinner } from "./loader";

type DocumentViewerProps = {
  documentType: DocumentType;
  signedUrl: string;
  highlights?: HighlightArea[];
};

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  signedUrl,
  highlights,
  documentType,
}) => {
  if (
    documentType === DocumentType.PDF ||
    documentType === DocumentType.EMAIL
  ) {
    return <DevinPdfViewer fileUrl={signedUrl} highlights={highlights} />;
  }

  if (documentType === DocumentType.CSV) {
    return <CSVViewer signedUrl={signedUrl} />;
  }

  if (documentType === DocumentType.XLSX) {
    return <ExcelViewer signedUrl={signedUrl} />;
  }

  return <div>Unknown file type: {documentType}</div>;
};

const ExcelViewer: React.FC<{
  signedUrl: string;
}> = ({ signedUrl }) => {
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);

  useEffect(() => {
    if (!signedUrl) return;

    const fetchData = async () => {
      try {
        const response = await fetch(signedUrl);
        const arrayBuffer = await response.arrayBuffer();
        setBytes(arrayBuffer);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [signedUrl]);

  return <ExcelPreview bytes={bytes} />;
};

const CSVViewer: React.FC<{
  signedUrl: string;
}> = ({ signedUrl }) => {
  const [data, setData] = useState<string[][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!signedUrl) return;

    const fetchData = async () => {
      try {
        const response = await fetch(signedUrl);
        const csvText = await response.text();
        Papa.parse(csvText, {
          complete: (result) => {
            setData(result.data as string[][]);
            setLoading(false);
          },
          error: (error: unknown) => {
            setError(error instanceof Error ? error.message : "Unknown error");
            setLoading(false);
          },
        });
      } catch (err) {
        setError("Failed to fetch CSV");
        setLoading(false);
      }
    };

    fetchData();
  }, [signedUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-16">
        <LoadingSpinner size="small" />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  if (data.length === 0) {
    return (
      <div className="p-4 text-muted-foreground">No CSV data available</div>
    );
  }

  return (
    <div className="overflow-auto border max-h-[calc(100dvh-10rem)]">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted">
          <tr>
            {data[0].map((header, index) => (
              <th
                key={header}
                className="px-4 py-2 text-left text-sm font-medium text-foreground"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-background divide-y divide-border">
          {data.slice(1).map((row, rowIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: data won't change
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  // biome-ignore lint/suspicious/noArrayIndexKey: data won't change
                  key={cellIndex}
                  className="px-4 py-2 text-sm text-foreground"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
